import { Database, Ruler, Sigma, Sparkles, type LucideIcon } from "lucide-react";
import type { ValueKind } from "../api/types";

// The four value kinds must never be confusable (docs/DESIGN_SYSTEM.md "Value-kind tag"). Each has its own word, icon
// and treatment — colour is a third signal, never the only one. "standard" is the Operations addition: a slate
// outline, because a governed constant is neither measured nor derived.

const META: Record<ValueKind, { label: string; short: string; icon: LucideIcon; className: string; explain: string }> = {
  recorded: {
    label: "Recorded",
    short: "rec",
    icon: Database,
    className: "border-slate-300 bg-white text-slate-600",
    explain: "Recorded — measured, punched or read from GSS."
  },
  standard: {
    label: "Standard",
    short: "std",
    icon: Ruler,
    className: "border-slate-400 bg-slate-50 text-slate-700",
    explain: "Standard — a governed constant (lead time, hours per day, capacity), never an average."
  },
  computed: {
    label: "Computed",
    short: "calc",
    icon: Sigma,
    className: "border-dashed border-slate-400 bg-slate-50 text-slate-600",
    explain: "Computed — derived by Summit Operations from recorded data."
  },
  predicted: {
    label: "Predicted",
    short: "pred",
    icon: Sparkles,
    className: "border-purple-200 bg-purple-50 text-purple-700 italic",
    explain: "Predicted — VIPER's suggestion. Never applied automatically and never signed by a machine."
  }
};

export function valueKindExplanation(kind: ValueKind): string {
  return META[kind].explain;
}

export function ValueKindBadge({ kind, compact = false, iconOnlyBelow2xl = false }: { kind: ValueKind; compact?: boolean; iconOnlyBelow2xl?: boolean }) {
  const meta = META[kind];
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded border px-1 py-px text-[10px] font-semibold leading-none tracking-wide ${meta.className}`}
      title={meta.explain}
      data-value-kind={kind}
    >
      <Icon aria-hidden="true" className="h-2.5 w-2.5" strokeWidth={2.5} />
      <span className={`uppercase ${iconOnlyBelow2xl ? "hidden 2xl:inline" : ""}`}>{compact ? meta.short : meta.label}</span>
      <span className="sr-only"> value. {meta.explain}</span>
    </span>
  );
}

export function ValueKindLegend({ kinds }: { kinds: readonly ValueKind[] }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500" aria-label="Value kinds used in this view">
      {kinds.map((k) => (
        <li key={k} className="inline-flex items-center gap-1.5">
          <ValueKindBadge kind={k} compact />
          <span>{META[k].explain.split(" — ")[1]}</span>
        </li>
      ))}
    </ul>
  );
}
